// src/pages/user/Settings.js
// Page Réglages complète - Compte, Apparence, Permissions, À propos - NE PAS MODIFIER

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../../components/shared/LanguageSwitcher';
import LocationPermissionModal from '../../components/shared/LocationPermissionModal';
import ShareSheet from '../../components/listing/ShareSheet';
import { useTheme } from '../../hooks/useTheme';
import { useAuth } from '../../hooks/useAuth';
import { getGeoPreference, setGeoPreference } from '../../utils/geoPreference';
import { APP_CONFIG } from '../../constants/config';

// Détecte si l'app tourne installée sur l'écran d'accueil (seul cas où iOS autorise les notifications web)
const isRunningAsInstalledApp = () => {
  if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) return true;
  if (window.navigator.standalone === true) return true; // Safari iOS spécifique
  return false;
};

const isIOSDevice = () => /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

const Settings = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { theme, isDark, toggleTheme } = useTheme();
  const { user, isAuthenticated, logout } = useAuth();

  const [geoEnabled, setGeoEnabled] = useState(true);
  const [browserDenied, setBrowserDenied] = useState(false);
  const [notifStatus, setNotifStatus] = useState('checking');
  const [showGeoGuide, setShowGeoGuide] = useState(false);
  const [showShareSheet, setShowShareSheet] = useState(false);

  const iosBlocked = isIOSDevice() && !isRunningAsInstalledApp();

  useEffect(() => {
    setGeoEnabled(getGeoPreference());

    if (navigator.permissions) {
      navigator.permissions.query({ name: 'geolocation' })
        .then((result) => {
          setBrowserDenied(result.state === 'denied');
          result.onchange = () => setBrowserDenied(result.state === 'denied');
        })
        .catch(() => {});
    }

    if (!('Notification' in window)) {
      setNotifStatus('unsupported');
    } else {
      setNotifStatus(Notification.permission);
    }
  }, []);

  const handleGeoToggle = () => {
    if (browserDenied) {
      setShowGeoGuide(true);
      return;
    }
    if (geoEnabled) {
      setGeoEnabled(false);
      setGeoPreference(false);
    } else {
      setGeoEnabled(true);
      setGeoPreference(true);
      navigator.geolocation.getCurrentPosition(
        () => {},
        (error) => { if (error.code === 1) setBrowserDenied(true); },
        { enableHighAccuracy: true, timeout: 8000 }
      );
    }
  };

  const handleNotifToggle = async () => {
    if (iosBlocked || notifStatus === 'unsupported') return;
    if (notifStatus === 'granted') return;
    if (notifStatus === 'denied') {
      alert("Les notifications sont bloquées au niveau du navigateur. Réactivez-les depuis les réglages du site.");
      return;
    }
    const result = await Notification.requestPermission();
    setNotifStatus(result);
  };

  const handleLogout = async () => {
    if (!window.confirm('Se déconnecter ?')) return;
    await logout();
    navigate('/');
  };

  const geoStatusLabel = browserDenied ? 'Bloqué' : geoEnabled ? 'Activé' : 'Désactivé';
  const geoStatusColor = browserDenied ? '#F56565' : geoEnabled ? '#48BB78' : theme.textMuted;

  const getNotifLabel = () => {
    if (iosBlocked) return 'Nécessite l\'installation';
    if (notifStatus === 'unsupported') return 'Non disponible';
    if (notifStatus === 'granted') return 'Activées';
    if (notifStatus === 'denied') return 'Bloquées';
    return 'Non activées';
  };
  const getNotifColor = () => {
    if (notifStatus === 'granted') return '#48BB78';
    if (notifStatus === 'denied') return '#F56565';
    return theme.textMuted;
  };

  const styles = {
    container: { display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: theme.bg, paddingBottom: '32px' },
    header: { display: 'flex', alignItems: 'center', gap: '12px', padding: '16px', position: 'sticky', top: 0, backgroundColor: theme.bg, zIndex: 50, borderBottom: `1px solid ${theme.borderSoft}` },
    backButton: { background: 'none', border: 'none', color: theme.textPrimary, fontSize: '20px', cursor: 'pointer', padding: '4px' },
    title: { fontSize: '20px', fontWeight: '700', color: theme.textPrimary },
    section: { padding: '8px 16px', display: 'flex', flexDirection: 'column', gap: '8px' },
    sectionTitle: { fontSize: '12px', fontWeight: '700', color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.4px', padding: '14px 4px 2px' },
    accountCard: {
      display: 'flex', alignItems: 'center', gap: '12px', padding: '16px',
      backgroundColor: theme.bgCard, borderRadius: '12px', border: `1px solid ${theme.borderSoft}`,
    },
    avatar: {
      width: '46px', height: '46px', borderRadius: '50%', backgroundColor: theme.goldSoft,
      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px',
      fontWeight: '700', color: theme.gold, flexShrink: 0,
    },
    accountInfo: { flex: 1, minWidth: 0 },
    accountName: { fontSize: '15px', fontWeight: '700', color: theme.textPrimary },
    accountEmail: { fontSize: '12px', color: theme.textSecondary, marginTop: '1px' },
    loginPrompt: { fontSize: '13px', color: theme.textSecondary, flex: 1 },
    loginBtn: { padding: '8px 16px', backgroundColor: theme.gold, border: 'none', borderRadius: '8px', color: '#FFFFFF', fontSize: '13px', fontWeight: '700', cursor: 'pointer' },
    logoutBtn: { padding: '7px 14px', backgroundColor: 'rgba(245, 101, 101, 0.1)', border: 'none', borderRadius: '8px', color: '#F56565', fontSize: '12px', fontWeight: '700', cursor: 'pointer' },
    row: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px', backgroundColor: theme.bgCard, borderRadius: '10px', border: `1px solid ${theme.borderSoft}` },
    rowLabel: { display: 'flex', alignItems: 'center', gap: '10px', fontSize: '15px', color: theme.textPrimary },
    rowMeta: { display: 'flex', alignItems: 'center', gap: '8px' },
    statusText: (color) => ({ fontSize: '12px', fontWeight: '600', color }),
    toggle: (active) => ({
      width: '48px', height: '28px', borderRadius: '14px', cursor: 'pointer', position: 'relative',
      transition: 'background-color 200ms', backgroundColor: active ? theme.gold : theme.textMuted,
    }),
    toggleKnob: (isOn) => ({
      position: 'absolute', top: '2px', left: isOn ? '22px' : '2px', width: '24px', height: '24px',
      borderRadius: '50%', backgroundColor: '#FFFFFF', transition: 'left 200ms',
    }),
    chevronRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px', backgroundColor: theme.bgCard, borderRadius: '10px', border: `1px solid ${theme.borderSoft}`, cursor: 'pointer' },
    chevron: { color: theme.textMuted, fontSize: '18px' },
    hintBox: { padding: '10px 14px', backgroundColor: theme.bgInput, borderRadius: '8px', fontSize: '12px', color: theme.textSecondary, lineHeight: '1.5' },
    versionText: { textAlign: 'center', fontSize: '11px', color: theme.textMuted, padding: '20px 0 4px' },
  };

  return (
    <div style={styles.container}>
      <Helmet>
        <title>Paramètres | Fynho</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div style={styles.header}>
        <button style={styles.backButton} onClick={() => navigate(-1)}>←</button>
        <div style={styles.title}>⚙️ {t('common.settings')}</div>
      </div>

      <div style={styles.section}>
        <div style={styles.sectionTitle}>Compte</div>
        {isAuthenticated ? (
          <div style={styles.accountCard}>
            <div style={styles.avatar}>{user?.name ? user.name.charAt(0).toUpperCase() : '👤'}</div>
            <div style={styles.accountInfo}>
              <div style={styles.accountName}>{user?.name}</div>
              <div style={styles.accountEmail}>{user?.email}</div>
            </div>
            <button style={styles.logoutBtn} onClick={handleLogout}>Déconnexion</button>
          </div>
        ) : (
          <div style={styles.accountCard}>
            <div style={styles.loginPrompt}>Connectez-vous pour sauvegarder vos favoris et avis.</div>
            <button style={styles.loginBtn} onClick={() => navigate('/login')}>Se connecter</button>
          </div>
        )}
      </div>

      <div style={styles.section}>
        <div style={styles.sectionTitle}>Général</div>
        <div style={styles.row}>
          <span style={styles.rowLabel}>🌍 {t('common.language')}</span>
          <LanguageSwitcher compact />
        </div>
        <div style={styles.row}>
          <span style={styles.rowLabel}>{isDark ? '🌙' : '☀️'} Thème sombre</span>
          <div style={styles.toggle(isDark)} onClick={toggleTheme}>
            <div style={styles.toggleKnob(isDark)} />
          </div>
        </div>
      </div>

      <div style={styles.section}>
        <div style={styles.sectionTitle}>Permissions de l'appareil</div>

        <div style={styles.row}>
          <span style={styles.rowLabel}>📍 Géolocalisation</span>
          <div style={styles.rowMeta}>
            <span style={styles.statusText(geoStatusColor)}>{geoStatusLabel}</span>
            <div style={styles.toggle(geoEnabled && !browserDenied)} onClick={handleGeoToggle}>
              <div style={styles.toggleKnob(geoEnabled && !browserDenied)} />
            </div>
          </div>
        </div>

        <div style={styles.row}>
          <span style={styles.rowLabel}>🔔 Notifications</span>
          <div style={styles.rowMeta}>
            <span style={styles.statusText(getNotifColor())}>{getNotifLabel()}</span>
            <div style={styles.toggle(notifStatus === 'granted')} onClick={handleNotifToggle}>
              <div style={styles.toggleKnob(notifStatus === 'granted')} />
            </div>
          </div>
        </div>

        {iosBlocked ? (
          <div style={styles.hintBox}>
            📱 Sur iPhone, les notifications ne fonctionnent que si Fynho est installée sur votre écran d'accueil.
            Ouvrez le menu de partage de Safari (icône ⬆️) puis "Sur l'écran d'accueil".
          </div>
        ) : (
          <div style={styles.hintBox}>
            La géolocalisation vous montre les annonces proches de vous. Les notifications vous alerteront
            bientôt des mises à jour de vos favoris et avis.
          </div>
        )}
      </div>

      <div style={styles.section}>
        <div style={styles.sectionTitle}>À propos</div>
        <div style={styles.chevronRow} onClick={() => setShowShareSheet(true)}>
          <span style={styles.rowLabel}>📤 Partager Fynho</span>
          <span style={styles.chevron}>›</span>
        </div>
        <div style={styles.chevronRow} onClick={() => navigate('/support')}>
          <span style={styles.rowLabel}>❓ Aide & Support</span>
          <span style={styles.chevron}>›</span>
        </div>
      </div>

      <div style={styles.versionText}>Fynho v{APP_CONFIG.APP_VERSION}</div>

      <LocationPermissionModal open={showGeoGuide} onClose={() => setShowGeoGuide(false)} />
      <ShareSheet
        open={showShareSheet}
        onClose={() => setShowShareSheet(false)}
        title="Fynho - Find Your New Home"
        url="https://fynho.com"
        imageUrl=""
      />
    </div>
  );
};

export default Settings;